import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useFormDraft } from "@/hooks/useFormDraft";
import { z } from "zod";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import type { GymCalendar } from "@/types/types";
import { getUserGymId } from "@/utils/gymAuth";
import {
  createCalendar,
  updateCalendar,
  type CalendarFormData,
} from "@/services/edgeFunctionWrappers/calendarsWrapper";

const formSchema = z.object({
  name: z.string().min(1, i18n.t("calendarFormDialog.nameRequired", { ns: "appui" })),
  description: z.string().optional(),
  color: z.string().optional(),
  is_active: z.boolean(),
  sort_order: z.coerce.number().min(0),
});

type FormValues = z.infer<typeof formSchema>;

interface CalendarFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingCalendar: GymCalendar | null;
  onSuccess: () => void;
}

export function CalendarFormDialog({
  open,
  onOpenChange,
  editingCalendar,
  onSuccess,
}: CalendarFormDialogProps) {
  const { t } = useTranslation("appui");
  const gymId = getUserGymId();

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      description: "",
      color: "#3b82f6",
      is_active: true,
      sort_order: 0,
    },
  });

  // Brouillon par calendrier (calendar-form:{id} / :new) superposé aux
  // valeurs du reset (qui re-tire à chaque open/close — inoffensif : le reset
  // pose isDirty=false donc aucun brouillon fantôme).
  const { clearDraft, mergeDraftOver } = useFormDraft<FormValues>("calendar-form", form, {
    entityId: editingCalendar?.id ?? null,
    restoreOnMount: false,
  });

  useEffect(() => {
    if (editingCalendar) {
      form.reset(mergeDraftOver({
        name: editingCalendar.name,
        description: editingCalendar.description ?? "",
        color: editingCalendar.color ?? "#3b82f6",
        is_active: editingCalendar.is_active,
        sort_order: editingCalendar.sort_order ?? 0,
      }));
    } else {
      form.reset(mergeDraftOver({
        name: "",
        description: "",
        color: "#3b82f6",
        is_active: true,
        sort_order: 0,
      }));
    }
  }, [editingCalendar, form, open, mergeDraftOver]);

  const onSubmit = async (values: FormValues) => {
    if (!gymId) return;

    try {
      const payload: CalendarFormData = {
        name: values.name,
        description: values.description || undefined,
        color: values.color || undefined,
        is_active: values.is_active,
        sort_order: values.sort_order,
      };

      if (editingCalendar) {
        await updateCalendar(gymId, editingCalendar.id, payload);
        toast.success(t("calendarFormDialog.updated"));
      } else {
        await createCalendar(gymId, payload);
        toast.success(t("calendarFormDialog.created"));
      }
      clearDraft();
      onSuccess();
      onOpenChange(false);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : t("calendarFormDialog.genericError");
      toast.error(message);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-base md:text-lg">
            {editingCalendar ? t("calendarFormDialog.editTitle") : t("calendarFormDialog.newTitle")}
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs md:text-sm">{t("calendarFormDialog.nameLabel")}</FormLabel>
                  <FormControl>
                    <Input placeholder={t("calendarFormDialog.namePlaceholder")} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs md:text-sm">{t("calendarFormDialog.descriptionLabel")}</FormLabel>
                  <FormControl>
                    <Textarea placeholder={t("calendarFormDialog.descriptionPlaceholder")} {...field} rows={2} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="color"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs md:text-sm">{t("calendarFormDialog.colorLabel")}</FormLabel>
                  <FormControl>
                    <div className="flex gap-2 items-center">
                      <input
                        type="color"
                        value={field.value ?? "#3b82f6"}
                        onChange={(e) => field.onChange(e.target.value)}
                        className="h-10 w-14 rounded border cursor-pointer"
                      />
                      <Input
                        placeholder="#3b82f6"
                        {...field}
                        className="flex-1 font-mono"
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="sort_order"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-xs md:text-sm">{t("calendarFormDialog.sortOrderLabel")}</FormLabel>
                  <FormControl>
                    <Input type="number" min={0} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 md:p-4">
                  <FormLabel className="text-sm md:text-base">{t("calendarFormDialog.activeLabel")}</FormLabel>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />
            <div className="flex justify-end gap-2 pt-2">
              {/* Annulation explicite : le brouillon est jeté. */}
              <Button type="button" variant="outline" onClick={() => { clearDraft(); onOpenChange(false); }}>
                {t("calendarFormDialog.cancel")}
              </Button>
              <Button type="submit">
                {editingCalendar ? t("calendarFormDialog.save") : t("calendarFormDialog.create")}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
